import { Locale } from "../Locale.ts";

/**
 * The words around a manipulative on a worksheet page (ADR 0117): the panel's heading and its
 * one-paragraph description, per kind. The tools' own labels are in one area each (baseTen,
 * fractionBars, clock, numberLine, doubleNumberLine).
 *
 * scripts/prerender.js bakes the English of these into the static page and cannot read this file;
 * prerender.test.js holds its copy equal to `en` below.
 */
const en = {
    "manipulatives.baseTenHeading": "Try it with base-ten blocks",
    "manipulatives.baseTenDescription":
        "Add and take away hundreds, tens and ones and watch the number change. Trade ten ones for a ten, or break a ten into ten ones: that trade is regrouping.",
    "manipulatives.fractionBarsHeading": "Try it with fraction bars",
    "manipulatives.fractionBarsDescription":
        "Split bars into halves, thirds, all the way to twelfths, and shade some of the pieces. Line the bars up under the whole to see which fractions are equal and which is larger.",
    "manipulatives.clockHeading": "Try it on a clock",
    "manipulatives.clockDescription":
        "Drag the minute hand, or use the buttons, and the hour hand moves with it as it does on a real clock. Hide the digital time to read the clock first, then check.",
    "manipulatives.numberLineHeading": "Try it on a number line",
    "manipulatives.numberLineDescription":
        "Move the point along the line, or hop right and left from it, and the line writes the number sentence for each hop.",
    "manipulatives.doubleNumberLineHeading": "Double number line",
    "manipulatives.doubleNumberLineDescription":
        "Two number lines that share their marks, so each mark pairs one amount with another. Stretch the lines to scale a ratio up, cut each step into equal parts to find a unit rate, or slide the marker to read a percent of a whole.",
} as const;

export type ManipulativesStringKey = keyof typeof en;

const es = {
    "manipulatives.baseTenHeading": "Pruébalo con bloques de base diez",
    "manipulatives.baseTenDescription":
        "Suma y quita centenas, decenas y unidades y mira cómo cambia el número. Cambia diez unidades por una decena, o rompe una decena en diez unidades: ese cambio es reagrupar.",
    "manipulatives.fractionBarsHeading": "Pruébalo con barras de fracciones",
    "manipulatives.fractionBarsDescription":
        "Divide las barras en mitades, tercios, hasta doceavos, y sombrea algunas partes. Alinea las barras debajo del entero para ver qué fracciones son iguales y cuál es mayor.",
    "manipulatives.clockHeading": "Pruébalo en un reloj",
    "manipulatives.clockDescription":
        "Arrastra el minutero, o usa los botones, y la manecilla de las horas se mueve con él, igual que en un reloj de verdad. Oculta la hora digital para leer primero el reloj y después comprobar.",
    "manipulatives.numberLineHeading": "Pruébalo en una recta numérica",
    "manipulatives.numberLineDescription":
        "Mueve el punto por la recta, o da saltos a la derecha y a la izquierda desde él, y la recta escribe la oración numérica de cada salto.",
    "manipulatives.doubleNumberLineHeading": "Recta numérica doble",
    "manipulatives.doubleNumberLineDescription":
        "Dos rectas numéricas que comparten sus marcas, así que cada marca une una cantidad con otra. Estira las rectas para ampliar una razón, divide cada paso en partes iguales para hallar una tasa unitaria, o desliza el marcador para leer un porcentaje de un entero.",
} satisfies Record<ManipulativesStringKey, string>;

const fr = {
    "manipulatives.baseTenHeading": "Essayez avec des blocs de base dix",
    "manipulatives.baseTenDescription":
        "Ajoutez et retirez des centaines, des dizaines et des unités, et regardez le nombre changer. Échangez dix unités contre une dizaine, ou cassez une dizaine en dix unités : cet échange, c'est le regroupement.",
    "manipulatives.fractionBarsHeading": "Essayez avec des barres de fractions",
    "manipulatives.fractionBarsDescription":
        "Partagez les barres en moitiés, en tiers, jusqu'aux douzièmes, et coloriez quelques parts. Alignez les barres sous l'entier pour voir quelles fractions sont égales et laquelle est la plus grande.",
    "manipulatives.clockHeading": "Essayez sur une horloge",
    "manipulatives.clockDescription":
        "Faites glisser la grande aiguille, ou utilisez les boutons : la petite aiguille la suit, comme sur une vraie horloge. Cachez l'heure numérique pour lire d'abord l'horloge, puis vérifier.",
    "manipulatives.numberLineHeading": "Essayez sur une droite numérique",
    "manipulatives.numberLineDescription":
        "Déplacez le point le long de la droite, ou faites des sauts à droite et à gauche à partir de lui, et la droite écrit l'égalité de chaque saut.",
    "manipulatives.doubleNumberLineHeading": "Double droite numérique",
    "manipulatives.doubleNumberLineDescription":
        "Deux droites numériques qui partagent leurs graduations : chaque graduation associe une quantité à une autre. Étirez les droites pour agrandir un rapport, coupez chaque pas en parts égales pour trouver un taux unitaire, ou glissez le repère pour lire un pourcentage d'un tout.",
} satisfies Record<ManipulativesStringKey, string>;

export const manipulativesStrings: Record<Locale, Record<ManipulativesStringKey, string>> = { en, es, fr };
